import * as React from 'react';

import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';

import ErrorBoundary from 'src/components/lib/error-boundary';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {ApiError, getApiErrorMessage} from 'src/utils/errors';

import css from './byod-error-boundary.css';


const NOOP = () => {
  // Fallback when ErrorBoundary doesn't provide a reset callback.
};

export type ByodErrorBoundaryVariant = 'page' | 'panel' | 'tile';

/** Required when `variant === 'tile'`. Determines fallback height + max-width. */
export type ByodTileKind = 'kpi' | 'viz';

export type ByodErrorBoundaryProps = {
  children: React.ReactNode;
  /** Controls fallback sizing/padding. `tile` is compact for inline per-tile boundaries. */
  variant?: ByodErrorBoundaryVariant;
  /** Sizes the tile fallback to match the row it sits in (kpi=128px, viz=420px). */
  tileKind?: ByodTileKind;
  /** Reset boundary state when this id changes (e.g. dashboard_id or placement_id). */
  resetKey?: unknown;
  /** Optional: custom retry handler. Defaults to clearing boundary state. */
  onReset?: () => void;
};

function getErrorMessage(error: Error, fallback: string): string {
  if (error instanceof ApiError) {
    return getApiErrorMessage(error) ?? fallback;
  }
  return fallback;
}

function getFallbackClassName(
  variant: ByodErrorBoundaryVariant,
  tileKind: ByodTileKind | undefined,
): string {
  if (variant !== 'tile') {
    return css.container;
  }
  // Default to viz sizing when the caller didn't specify a tile kind.
  return tileKind === 'kpi' ? css.tileKpi : css.tileViz;
}

function ByodErrorFallback({
  error,
  reset,
  variant,
  tileKind,
}: {
  error: Error;
  reset: () => void;
  variant: ByodErrorBoundaryVariant;
  tileKind: ByodTileKind | undefined;
}) {
  const {t} = useLabelTranslation();

  const title = t('Something_went_wrong', 'Something went wrong');
  const description = getErrorMessage(
    error,
    t(
      'An_unexpected_error_occurred_Please_try_again',
      'An unexpected error occurred. Please try again.',
    ),
  );

  const imageVariant = variant === 'tile' ? 'chart' : 'data';

  return (
    <div
      className={getFallbackClassName(variant, tileKind)}
      data-testid="byod-error-fallback"
    >
      <EmptyState
        title={title}
        description={description}
        imageVariant={imageVariant}
      >
        <div className={css.retryWrap}>
          <Button
            type="secondary"
            size="small"
            iconLeftName="arrow-rotate-right"
            iconLeftType="regular"
            onClick={reset}
            testId="byod-error-fallback-retry"
          >
            {t('Retry', 'Retry')}
          </Button>
        </div>
      </EmptyState>
    </div>
  );
}

export function ByodErrorBoundary({
  children,
  variant = 'page',
  tileKind,
  resetKey,
  onReset,
}: ByodErrorBoundaryProps) {
  return (
    <ErrorBoundary
      id={resetKey}
      onReset={onReset}
      fallback={(error, reset) => (
        <ByodErrorFallback
          error={error}
          reset={reset ?? NOOP}
          variant={variant}
          tileKind={tileKind}
        />
      )}
    >
      {children}
    </ErrorBoundary>
  );
}
